import dayjs from "@gtmhub/dayjs";
import { ITeam } from "@gtmhub/teams";
import { toKeyMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { IReflectionContributor } from "../models/reflection-contributor-list.models";
import { ReflectionPanel } from "../models/reflection-panel.models";
import { ReflectionOverviewFilterName } from "../models/reflections.models";

export function startWithFieldValue<T extends { [K in keyof T] }>(field: keyof T, valueToStartWith: T[keyof T]): (a: T, b: T) => number {
  return function (a: T, b: T): number {
    return a[field] === valueToStartWith ? -1 : b[field] === valueToStartWith ? 1 : 0;
  };
}

export const extractUniqueTeamMemberIds = (team: ITeam): string[] => {
  return [...new Set([...(team.members || []), ...(team.manager ? [team.manager] : [])])];
};

export const toEnrichedContributorListMember = (member: Assignee, params = { withDotIndicator: false }): IReflectionContributor => ({
  ...member,
  withDotIndicator: params.withDotIndicator,
});

export const getLastMondayAsDate = (): string => {
  return dayjs().startOf("isoWeek").toISOString();
};

export const getLastEditableMondayAsDate = (): string => {
  return dayjs().startOf("isoWeek").subtract(7, "days").toISOString();
};

export const createdAtBelongsInCadance = (createdAt: string, cadanceStart: string, cadanceEnd: string): boolean => {
  return dayjs(createdAt).isBetween(cadanceStart, cadanceEnd, "days", "[]");
};

export const getLastDayOfCadence = (cadenceStartDate: string, cadenceDurationInDays: number): string => {
  const startDate = dayjs(cadenceStartDate).utc();
  const endDate = startDate
    .clone()
    .add(cadenceDurationInDays - 1, "day")
    .add(23, "hours")
    .add(59, "minutes")
    .add(59, "seconds");

  return adjustWithOneHourIfNecessary(startDate, endDate);
};

export const getPrevCadenceStartDate = (currCadenceStartDate: string, cadenceDurationInDays: number): string => {
  const currStartDate = dayjs(currCadenceStartDate).utc();
  const prevStartDate = currStartDate.clone().subtract(cadenceDurationInDays, "day");

  return adjustWithOneHourIfNecessary(currStartDate, prevStartDate);
};

export const getFilteredByNoReflectionLength = (reflection: ReflectionPanel[]): number => {
  return reflection.filter((reflection) => reflection.noReflection)?.length || 0;
};

export const getFilteredByReflectionLength = (reflection: ReflectionPanel[]): number => {
  return reflection.filter((reflection) => !reflection.noReflection)?.length || 0;
};

export const getFilteredByNoteTypeLength = (reflection: ReflectionPanel[], filter: ReflectionOverviewFilterName): number => {
  return reflection.filter((reflection) => toKeyMap("type", reflection.notes)[filter]?.text || toKeyMap("type", reflection.notes)[filter]?.gif?.id)?.length || 0;
};

const adjustWithOneHourIfNecessary = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): string => {
  const shouldAdjustForward = shouldAdjustForDST(startDate, endDate);
  const shouldAdjustBackwards = shouldAdjustForDST(endDate, startDate);

  if (shouldAdjustForward) {
    return endDate.add(1, "hour").toISOString();
  } else if (shouldAdjustBackwards) {
    return endDate.subtract(1, "hour").toISOString();
  }

  return endDate.toISOString();
};

const shouldAdjustForDST = (date1: dayjs.Dayjs, date2: dayjs.Dayjs): boolean => {
  return isDateInDST(date1) && !isDateInDST(date2);
};

const isDateInDST = (date: dayjs.Dayjs): boolean => {
  const year = date.year();
  const stdDate = dayjs(`${year}-01-01`); // January 1st (standard time)
  const dstDate = dayjs(`${year}-07-01`); // July 1st (daylight saving time)

  const stdOffset = Math.max(stdDate.utcOffset(), dstDate.utcOffset());
  const currentOffset = date.utcOffset();

  return currentOffset !== stdOffset;
};
